import { resolveSocialLinks } from "@/lib/socialPlatforms";

type SocialLinkRowVariant = "header" | "footer" | "block";

interface SocialLinkRowProps {
  /** Stored `{ platformKey: url }` map from tenant settings or block content. */
  links: Record<string, string> | undefined | null;
  variant?: SocialLinkRowVariant;
  className?: string;
}

/**
 * Accessible icon row for social & streaming profiles. Compact icon-only in
 * header/footer (name carried by aria-label + tooltip); icon + visible label
 * in the `block` variant so streaming services are never ambiguous. Icons use
 * `currentColor`, so they inherit theme colors and pass contrast in both modes.
 */
export function SocialLinkRow({ links, variant = "footer", className }: SocialLinkRowProps) {
  const resolved = resolveSocialLinks(links);
  if (resolved.length === 0) return null;

  const showLabel = variant === "block";
  const gap = variant === "block" ? "gap-3" : "gap-1";

  return (
    <ul className={`flex flex-wrap items-center ${gap} ${className ?? ""}`}>
      {resolved.map(({ key, label, href, Icon }) => (
        <li key={key}>
          <a
            href={href}
            target={href.startsWith("http") ? "_blank" : undefined}
            rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
            aria-label={label}
            title={label}
            className={
              showLabel
                ? "inline-flex min-h-[44px] items-center gap-2 rounded-[var(--border-radius)] border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-text)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]"
                : "inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-[var(--color-secondary)] hover:bg-[var(--color-border)]/50 hover:text-[var(--color-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]"
            }
          >
            <Icon className="h-5 w-5" aria-hidden />
            {showLabel && <span>{label}</span>}
          </a>
        </li>
      ))}
    </ul>
  );
}
